import axios from 'axios';
import { connectIDConfig } from '../config/connectid';

export interface ConnectIDAuthResponse {
  authUrl: string;
}

export interface ConnectIDUserDetails {
  token: {
    raw: string;
    decoded: any;
  };
  claims: Record<string, any>;
  xFapiInteractionId: string;
}

export interface Participant {
  id: string;
  name: string;
  status: string;
  authorisationServerId: string;
}

class ConnectIDService {
  private baseUrl: string;
  private requestConfig: any;

  constructor() {
    this.baseUrl = `${connectIDConfig.baseUrl}/v1/connectid`;
    this.requestConfig = {
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json',
        'Origin': connectIDConfig.origin
      }
    };
  }

  /**
   * Initiates the bank selection process
   * @param authorisationServerId The ID of the selected bank's authorization server
   * @param essentialClaims The claims to request from the user
   * @returns Promise with the authorization URL
   */
  async selectBank(authorisationServerId: string, essentialClaims: string[] = []): Promise<ConnectIDAuthResponse> {
    try {
      // console.log('ConnectIDService.selectBank called with:', { authorisationServerId, essentialClaims });
      
      const response = await axios.post(
        `${this.baseUrl}/select-bank`, 
        {
          authorisationServerId,
          essentialClaims
        }, 
        this.requestConfig
      );
      
      // console.log('ConnectIDService.selectBank response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error selecting bank:', error);
      if (axios.isAxiosError(error) && error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
      }
      throw error;
    }
  }

  /**
   * Retrieves user details after successful authentication
   * @param code The authorization code
   * @param state The state parameter
   * @param iss The issuer
   * @returns Promise with the user details
   */
  async retrieveUserDetails(code: string, state: string, iss: string): Promise<ConnectIDUserDetails> {
    try {
      // console.log('ConnectIDService.retrieveUserDetails called with:', { code, state, iss });
      
      const response = await axios.get(
        `${this.baseUrl}/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}&iss=${encodeURIComponent(iss)}`,
        this.requestConfig
      );
      
      // console.log('ConnectIDService.retrieveUserDetails response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error retrieving user details:', error);
      if (axios.isAxiosError(error) && error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
      }
      throw error;
    }
  }

  /**
   * Fetches the list of available banks
   * @returns Promise with the list of participants
   */
  async getParticipants(): Promise<Participant[]> {
    try {
      // console.log('ConnectIDService.getParticipants called');
      
      const response = await axios.get(
        `${this.baseUrl}/participants`, 
        this.requestConfig
      );
      
      // console.log('ConnectIDService.getParticipants response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching participants:', error);
      if (axios.isAxiosError(error) && error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
      }
      throw error;
    }
  }

  /**
   * Verifies a user's identity using ConnectID
   * @param token The ConnectID token
   * @param userData The user data to verify
   * @returns Promise with the verification result
   */
  async verifyIdentity(token: string, userData: any): Promise<any> {
    try {
      // console.log('ConnectIDService.verifyIdentity called with:', { userData });
      
      const response = await axios.post(
        `${this.baseUrl}/verify`,
        userData,
        {
          headers: {
            Authorization: `Bearer ${token}`
          },
          withCredentials: true
        }
      );
      
      // console.log('ConnectIDService.verifyIdentity response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error verifying identity:', error);
      if (axios.isAxiosError(error) && error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
      }
      throw error;
    }
  }
}

export default new ConnectIDService();
